import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { UserPlus, Shield, Phone, Mail, Trash2, Coffee } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";

export default function TeamPage() {
  const { user } = useAuth();
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "agent",
  });
  const qc = useQueryClient();

  const usersQ = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const leadsQ = useQuery({
    queryKey: ["leads", { limit: 0 }],
    queryFn: async () => (await api.get("/leads", { params: { limit: 0 } })).data,
  });

  // Count leads per user for a small stat
  const perUserQ = useQuery({
    queryKey: ["leads-per-user"],
    queryFn: async () => {
      const r = await api.get("/leads", { params: { limit: 1000 } });
      const map = {};
      for (const l of r.data.items) {
        if (l.assigned_to) map[l.assigned_to] = (map[l.assigned_to] || 0) + 1;
      }
      return map;
    },
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/users", form)).data,
    onSuccess: () => {
      toast.success("Team member added");
      setAddOpen(false);
      setForm({ name: "", email: "", password: "", phone: "", role: "agent" });
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Add failed"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: (data) => {
      const n = data?.leads_released || 0;
      toast.success(
        n > 0
          ? `Member removed · ${n} lead${n === 1 ? "" : "s"} released back to New`
          : "Member removed"
      );
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const offMut = useMutation({
    mutationFn: async ({ id, is_off }) =>
      (await api.patch(`/users/${id}`, { is_off })).data,
    // Optimistic update so the toggle feels instant.
    onMutate: async ({ id, is_off }) => {
      await qc.cancelQueries({ queryKey: ["users"] });
      const prev = qc.getQueryData(["users"]);
      qc.setQueryData(["users"], (old) =>
        Array.isArray(old) ? old.map((u) => (u.id === id ? { ...u, is_off } : u)) : old
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["users"], ctx.prev);
      toast.error(err?.response?.data?.detail || "Update failed");
    },
    onSuccess: (data) => {
      toast.success(data.is_off ? `${data.name} marked Off` : `${data.name} marked In`);
      qc.invalidateQueries({ queryKey: ["users"] });
    },
  });

  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-5" data-testid="team-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-navy lg:text-3xl">Team</h2>
        {isAdmin && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-member-btn" className="bg-navy hover:brightness-110">
                <UserPlus className="h-4 w-4" /> Add member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add team member</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Full name</Label>
                  <Input
                    data-testid="new-member-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input
                    data-testid="new-member-email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label className="text-xs">Phone</Label>
                  <Input
                    data-testid="new-member-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="new-member-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Temporary password</Label>
                    <Input
                      data-testid="new-member-password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
                <Button
                  data-testid="save-new-member"
                  className="bg-navy"
                  disabled={
                    !form.name || !form.email || !form.password || createMut.isPending
                  }
                  onClick={() => createMut.mutate()}
                >
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {(usersQ.data || []).map((u) => (
          <div
            key={u.id}
            className={`soft-card p-4 ${u.is_off ? "ring-1 ring-amber-200/70 bg-amber-50/30" : ""}`}
            data-testid={`member-card-${u.id}`}
          >
            <div className="flex items-center gap-3">
              <Avatar className="h-11 w-11 bg-[hsl(var(--brand-navy))]">
                <AvatarFallback className="bg-[hsl(var(--brand-navy))] text-sm text-white">
                  {u.initials || u.name[0]}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-navy">{u.name}</span>
                  {u.role === "admin" && (
                    <Badge className="border-transparent bg-amber-100 text-[10px] font-semibold text-amber-800 hover:bg-amber-100">
                      <Shield className="mr-1 h-2.5 w-2.5" /> Admin
                    </Badge>
                  )}
                  {u.is_off && (
                    <Badge
                      data-testid={`member-off-badge-${u.id}`}
                      className="border-transparent bg-amber-100 text-[10px] font-semibold text-amber-900 hover:bg-amber-100"
                    >
                      <Coffee className="mr-1 h-2.5 w-2.5" /> Off
                    </Badge>
                  )}
                </div>
                <div className="mt-0.5 truncate text-xs text-slate-500">{u.email}</div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
              {u.phone && (
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {u.phone}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3 w-3" /> {u.is_active ? "Active" : "Disabled"}
              </span>
            </div>

            {isAdmin && u.id !== user?.id && (
              <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-700">Off today</div>
                  <div className="text-[11px] text-slate-500">
                    {u.is_off ? "New leads will skip this user" : "Available for new leads"}
                  </div>
                </div>
                <Switch
                  data-testid={`member-off-toggle-${u.id}`}
                  checked={Boolean(u.is_off)}
                  disabled={offMut.isPending}
                  onCheckedChange={(v) => offMut.mutate({ id: u.id, is_off: v })}
                />
              </div>
            )}

            <div className="mt-3 flex items-center justify-between border-t border-dashed border-slate-100 pt-3">
              <div>
                <div className="text-[11px] text-slate-500">Leads assigned</div>
                <div className="text-lg font-bold text-navy">
                  {perUserQ.data?.[u.id] ?? 0}
                </div>
              </div>
              {isAdmin && u.id !== user?.id && (
                <button
                  data-testid={`delete-member-${u.id}`}
                  onClick={() => {
                    if (window.confirm(`Remove ${u.name}?`)) delMut.mutate(u.id);
                  }}
                  className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                  aria-label={`Delete ${u.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
